import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';
import { immer } from 'zustand/middleware/immer';
import type { User, UserPreferences } from '@/types';
import { authService } from '@/services';

// ============================================
// AUTH STATE INTERFACE
// ============================================

interface AuthState {
  // State
  user: User | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  login: (email: string, password: string) => Promise<void>;
  register: (data: {
    companyName: string;
    legalName: string;
    taxId: string;
    email: string;
    phone: string;
    city: string;
    adminEmail: string;
    adminPassword: string;
    adminFirstName: string;
    adminLastName: string;
  }) => Promise<void>;
  logout: () => Promise<void>;
  refreshAuth: () => Promise<void>;
  setTokens: (accessToken: string, refreshToken: string) => void;
  setUser: (user: User) => void;
  updateProfile: (data: Partial<User>) => Promise<void>;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
  clearError: () => void;
  checkAuth: () => Promise<boolean>;
}

// ============================================
// AUTH STORE
// ============================================

export const useAuthStore = create<AuthState>()(
  persist(
    immer((set, get) => ({
      // Initial state
      user: null,
      token: null,
      refreshToken: null,
      isAuthenticated: false,
      isLoading: false,
      error: null,
      
      // Login action
      login: async (email: string, password: string) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        
        try {
          const response = await authService.login({ email, password }) as any;
          
          set((state) => {
            state.user = response.user;
            state.token = response.accessToken;
            state.refreshToken = response.refreshToken;
            state.isAuthenticated = true;
            state.isLoading = false;
          });
          
          // Fetch full profile with permissions
          try {
            const profile = await authService.getProfile();
            set((state) => {
              state.user = profile;
            });
          } catch (profileError) {
            // Ignore profile fetch errors
            console.warn('Failed to fetch profile:', profileError);
          }
        } catch (error: any) {
          set((state) => {
            state.isLoading = false;
            state.error = error.response?.data?.message || 'Échec de la connexion';
          });
          throw error;
        }
      },
      
      // Register action
      register: async (data) => {
        set((state) => {
          state.isLoading = true;
          state.error = null;
        });
        
        try {
          const response = await authService.register(data) as any;
          
          set((state) => {
            state.user = response.user;
            state.token = response.accessToken;
            state.refreshToken = response.refreshToken;
            state.isAuthenticated = true;
            state.isLoading = false;
          });
        } catch (error: any) {
          set((state) => {
            state.isLoading = false;
            state.error = error.response?.data?.message || "Échec de l'inscription";
          });
          throw error;
        }
      },
      
      // Logout action
      logout: async () => {
        try {
          await authService.logout();
        } catch (error) {
          // Ignore logout errors
        } finally {
          set((state) => {
            state.user = null;
            state.token = null;
            state.refreshToken = null;
            state.isAuthenticated = false;
            state.error = null;
          });
          
          // Clear localStorage
          localStorage.removeItem('tenant');
        }
      },
      
      // Refresh authentication
      refreshAuth: async () => {
        const { refreshToken } = get();
        
        if (!refreshToken) {
          throw new Error('No refresh token');
        }
        
        try {
          const tokens = await authService.refreshToken(refreshToken);
          
          set((state) => {
            state.token = tokens.accessToken;
            state.refreshToken = tokens.refreshToken;
          });
        } catch (error) {
          // If refresh fails, logout
          get().logout();
          throw error;
        }
      },
      
      // Set tokens manually
      setTokens: (accessToken: string, refreshToken: string) => {
        set((state) => {
          state.token = accessToken;
          state.refreshToken = refreshToken;
        });
      },
      
      // Set user manually
      setUser: (user: User) => {
        set((state) => {
          state.user = user;
          state.isAuthenticated = true;
        });
      },
      
      // Update user profile
      updateProfile: async (data: Partial<User>) => {
        set((state) => {
          state.isLoading = true;
        });
        
        try {
          const updatedUser = await authService.updateProfile(data);
          
          set((state) => {
            state.user = updatedUser;
            state.isLoading = false;
          });
        } catch (error: any) {
          set((state) => {
            state.isLoading = false;
            state.error = error.response?.data?.message || 'Échec de la mise à jour du profil';
          });
          throw error;
        }
      },
      
      // Update user preferences
      updatePreferences: async (preferences: Partial<UserPreferences>) => {
        const { user } = get();
        
        if (!user) return;
        
        try {
          const updatedUser = await authService.updateProfile({
            preferences: { ...user.preferences, ...preferences },
          });
          
          set((state) => {
            state.user = updatedUser;
          });
        } catch (error) {
          throw error;
        }
      },
      
      // Clear error
      clearError: () => {
        set((state) => {
          state.error = null;
        });
      },
      
      // Check if user is authenticated
      checkAuth: async () => {
        const { token, refreshToken } = get();
        
        if (!token) {
          return false;
        }
        
        try {
          const user = await authService.getProfile();
          
          set((state) => {
            state.user = user;
            state.isAuthenticated = true;
          });
          
          return true;
        } catch (error) {
          // Try to refresh token
          if (refreshToken) {
            try {
              await get().refreshAuth();
              const user = await authService.getProfile();
              
              set((state) => {
                state.user = user;
                state.isAuthenticated = true;
              });
              
              return true;
            } catch {
              await get().logout();
              return false;
            }
          }
          
          await get().logout();
          return false;
        }
      },
    })),
    {
      name: 'auth-storage',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        token: state.token,
        refreshToken: state.refreshToken,
        user: state.user,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);

// ============================================
// SELECTORS
// ============================================

export const selectUser = (state: AuthState) => state.user;
export const selectIsAuthenticated = (state: AuthState) => state.isAuthenticated;
export const selectIsLoading = (state: AuthState) => state.isLoading;
export const selectError = (state: AuthState) => state.error;
export const selectToken = (state: AuthState) => state.token;

// ============================================
// PERMISSION HELPERS
// ============================================

export const usePermissions = () => {
  const user = useAuthStore(selectUser);
  
  const hasPermission = (permission: string): boolean => {
    if (!user) return false;
    
    // Super admin or system:admin has all permissions
    if (user.isSuperAdmin) return true;
    
    const permissions = user.permissions || [];
    
    // system:admin permission grants all access
    if (permissions.includes('system:admin')) return true;
    
    // Check specific permission
    return permissions.includes(permission);
  };
  
  const hasAnyPermission = (permissions: string[]): boolean => {
    return permissions.some(hasPermission);
  };
  
  const hasAllPermissions = (permissions: string[]): boolean => {
    return permissions.every(hasPermission);
  };
  
  const hasRole = (role: string | string[]): boolean => {
    if (!user) return false;
    
    // Handle role as object with code property
    const userRoleCode = typeof user.role === 'object' && user.role && 'code' in user.role 
      ? (user.role as any).code 
      : user.role;
    
    if (Array.isArray(role)) {
      return role.includes(userRoleCode as string);
    }
    
    return userRoleCode === role;
  };
  
  return {
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    permissions: user?.permissions || [],
    role: user?.role,
  };
};
